// Light, dark, or whatever the machine is set to.
//
// The choice is stamped on <html data-theme>, which is what the token overrides
// in app.css key off, and remembered in localStorage. Excalidraw is told the
// same thing, so the canvas is not a white rectangle in the middle of a dark
// app.

import { useCallback, useEffect, useState } from "react";

const KEY = "hmc.theme";
export const THEMES = ["system", "light", "dark"];

const query = () => window.matchMedia("(prefers-color-scheme: dark)");

export const readStoredTheme = () => {
  try {
    const stored = window.localStorage.getItem(KEY);
    return THEMES.includes(stored) ? stored : "system";
  } catch {
    // Safari in private mode throws on localStorage. A theme is not worth an
    // error screen.
    return "system";
  }
};

const resolve = (theme) => (theme === "system" ? (query().matches ? "dark" : "light") : theme);

const stamp = (theme) => {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
};

/**
 * @returns {{theme: string, resolved: "light"|"dark", setTheme: Function, cycle: Function}}
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [resolved, setResolved] = useState(() => resolve(readStoredTheme()));

  useEffect(() => {
    stamp(theme);
    setResolved(resolve(theme));
    try {
      window.localStorage.setItem(KEY, theme);
    } catch {
      /* not worth an error screen */
    }
  }, [theme]);

  // Following the machine means following it as it changes, not only at load.
  useEffect(() => {
    if (theme !== "system") return undefined;
    const media = query();
    const onChange = () => setResolved(media.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = useCallback((next) => setThemeState(THEMES.includes(next) ? next : "system"), []);
  const cycle = useCallback(
    () => setThemeState((current) => THEMES[(THEMES.indexOf(current) + 1) % THEMES.length]),
    [],
  );

  return { theme, resolved, setTheme, cycle };
};
